import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { ChevronUp, ExternalLink, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { LEADERBOARD } from "@/constants/testIds";

const BASE = API_BASE.replace(/\/api$/, "");

export default function CandidateProfile() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [lightbox, setLightbox] = useState(-1);
  const [voteFor, setVoteFor] = useState(false);
  const [phase, setPhase] = useState("request");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/candidates/${id}`);
      setC(data);
    } catch (e) {
      console.error("candidate fetch failed:", e);
    }
  };
  useEffect(() => { refresh(); }, [id]);

  const closeModal = () => { setVoteFor(false); setPhase("request"); setEmail(""); setName(""); setCode(""); setDevCode(""); };

  const requestCode = async () => {
    if (!email || !name) { toast.error("Email + name required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/candidates/${id}/vote/request`, { email, name, consent_marketing: true });
      setDevCode(data?.dev_code || "");
      setPhase("verify");
      toast.success("Verification code sent.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const verifyVote = async () => {
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/vote/verify`, { email, code });
      toast.success("Vote locked ⚡");
      await refresh();
      closeModal();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  if (!c) {
    return <Layout><div className="px-6 py-20 text-white/60">Loading candidate profile…</div></Layout>;
  }

  const portfolio = c.portfolio || [];

  return (
    <Layout>
      <section className="relative py-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <Link to="/leaderboard" className="text-[#C9A227] text-xs tracking-[0.25em] uppercase font-bold hover:underline">
            ← Back to Leaderboard
          </Link>

          <div className="mt-8 grid md:grid-cols-[1.5fr_1fr] gap-10 items-start">
            <div>
              <span className="label-gold">Admiral Candidate</span>
              <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white mt-2 leading-[0.95]">
                {c.name}
              </h1>
              <p className="text-white/70 mt-4 max-w-xl">{c.niche}</p>

              <div className="flex flex-wrap gap-4 mt-6 text-[10px] tracking-[0.25em] uppercase">
                {c.tiktok && <span className="text-white/60">TikTok <span className="text-[#C9A227] font-bold">{c.tiktok}</span></span>}
                {c.instagram && <span className="text-white/60">IG <span className="text-[#C9A227] font-bold">{c.instagram}</span></span>}
                {c.youtube && <span className="text-white/60">YT <span className="text-[#C9A227] font-bold">{c.youtube}</span></span>}
                {c.follower_count && <span className="text-[#C9A227] font-bold">{c.follower_count} followers</span>}
              </div>

              {c.narrative_preview && (
                <div className="mt-8 border-l-2 border-[#C9A227] pl-5 py-2">
                  <p className="text-white/85 italic leading-relaxed">"{c.narrative_preview}"</p>
                </div>
              )}

              {c.video_link && (
                <a href={c.video_link} target="_blank" rel="noreferrer" className="btn-outline-gold inline-flex items-center gap-2 mt-8 text-xs">
                  60-Second Vision Video <ExternalLink size={14}/>
                </a>
              )}
            </div>

            <div className="glass p-8 border border-[#C9A227]/40 sticky top-24">
              <div className="text-center">
                <div className="text-6xl font-black gold-shimmer tabular-nums">▲ {c.upvotes}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mt-1">Public Upvotes</div>
              </div>
              <button onClick={() => setVoteFor(true)} className="btn-gold w-full mt-6 text-xs">
                Cast Your Upvote
              </button>
              <p className="text-[10px] text-white/40 mt-3 text-center tracking-widest uppercase">Email-verified · 1 per voter</p>
            </div>
          </div>

          {/* Aesthetic Vault gallery */}
          {portfolio.length > 0 && (
            <div className="mt-16">
              <span className="label-gold">Aesthetic Vault · Portfolio</span>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-6">
                {portfolio.map((p, i) => (
                  <button key={p.id || i} onClick={() => setLightbox(i)}
                    className="relative aspect-square overflow-hidden border border-[#C9A227]/20 hover:border-[#C9A227] transition-all group">
                    <img src={`${BASE}${p.url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox >= 0 && portfolio[lightbox] && (
        <div className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(-1)}>
          <button onClick={() => setLightbox(-1)} className="absolute top-6 right-6 text-white hover:text-[#C9A227]" aria-label="Close">
            <X size={28}/>
          </button>
          <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => Math.max(0, i - 1)); }} disabled={lightbox === 0}
            className="absolute left-6 text-white/70 hover:text-[#C9A227] disabled:opacity-20">
            <ChevronLeft size={36}/>
          </button>
          <img src={`${BASE}${portfolio[lightbox].url}`} alt="" className="max-h-[88vh] max-w-[92vw] object-contain border border-[#C9A227]/40" onClick={(e) => e.stopPropagation()}/>
          <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => Math.min(portfolio.length - 1, i + 1)); }} disabled={lightbox === portfolio.length - 1}
            className="absolute right-6 text-white/70 hover:text-[#C9A227] disabled:opacity-20">
            <ChevronRight size={36}/>
          </button>
          <div className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-xs tracking-[0.3em] uppercase">
            {lightbox + 1} / {portfolio.length}
          </div>
        </div>
      )}

      {/* Vote modal */}
      {voteFor && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={closeModal}>
          <div className="glass border border-[#C9A227]/40 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <span className="label-gold">Viral Strike · Cast Vote</span>
            <h3 className="text-white text-2xl font-black uppercase tracking-tighter mt-2">{c.name}</h3>
            {phase === "request" && (
              <div className="space-y-4 mt-6">
                <p className="text-white/55 text-sm">Email-verified votes only. We add you to the Sovereign Audience.</p>
                <div><label className="label-gold">Your Name</label><input className="siren-input" value={name} onChange={(e) => setName(e.target.value)}/></div>
                <div><label className="label-gold">Email</label><input className="siren-input" value={email} onChange={(e) => setEmail(e.target.value)}/></div>
                <button onClick={requestCode} disabled={busy} className="btn-gold w-full text-xs">
                  {busy ? "Sending…" : "Send Verification Code"}
                </button>
              </div>
            )}
            {phase === "verify" && (
              <div className="space-y-4 mt-6">
                {devCode && (
                  <div className="p-3 border border-[#C9A227]/30 bg-black/40">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1">Dev Mode Code</div>
                    <div className="text-[#C9A227] font-black tracking-[0.4em] text-lg">{devCode}</div>
                  </div>
                )}
                <input className="siren-input tracking-[0.3em] text-center text-lg font-bold" placeholder="000000"
                  value={code} onChange={(e) => setCode(e.target.value)}/>
                <button onClick={verifyVote} disabled={busy} className="btn-gold w-full text-xs">
                  {busy ? "Verifying…" : "Confirm Upvote"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
